import React from "react";

const FixingVerticalScrollSnapBackBugChattyBooksReader = () => {
  return (
    <div className="article-body">
      <p className="text-sm text-gray-500 mb-4">February 20th, 2026 • Developer Blogs • Patrick</p>
      
      <p>
        Today I fixed a subtle but frustrating bug in the Chatty Books reader: when scrolling vertically through a book, the page would occasionally snap back to page 1, losing the reader’s position. The issue turned out to be a race condition between the IntersectionObserver that tracks visible pages and the scroll‑event handlers that manage horizontal/vertical mode switching. This post walks through the diagnosis, the fix, and the broader lesson about synchronizing scroll‑tracking logic in modern web apps.
      </p>

      <h2>The Bug: Vertical Scroll Would Snap Back to Page 1</h2>

      <p>
        The Chatty Books reader supports two scrolling modes: <strong>horizontal</strong> (swipe‑left/swipe‑right, one page at a time) and <strong>vertical</strong> (continuous scroll, like a web page). The bug appeared only in vertical mode. A user would scroll several pages down, pause, and—without any interaction—the view would jump back to the first page. This made reading anything longer than a few screens impossible.
      </p>

      <p>
        The snap‑back was inconsistent. It didn’t happen on every scroll, only when the scroll started before the IntersectionObserver had fully “settled.” That inconsistency pointed to a timing problem: something was resetting the scroll position after the fact.
      </p>

      <h2>Diagnosis: IntersectionObserver vs. Scroll‑Event Race</h2>

      <p>
        The reader uses an IntersectionObserver to track which page is currently in view. That information drives the table‑of‑contents highlight, the progress bar, and the “current page” indicator. The observer is set up in a <code>useEffect</code> hook that runs when the component mounts.
      </p>

      <p>
        The bug’s root cause was a guard inside that observer’s callback:
      </p>

<pre><code>{`useEffect(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      // ⚠️ This guard was causing the problem
      if (!hasScrolledToInitialPage) return;

      // … track visible page …
    },
    { threshold: 0.5 }
  );

  // Observe each page element
  pages.forEach((page) => observer.observe(page));

  return () => observer.disconnect();
}, [hasScrolledToInitialPage]);`}</code></pre>

      <p>
        The <code>hasScrolledToInitialPage</code> flag was introduced to prevent the observer from firing before the reader had scrolled to the user’s saved position (e.g., “continue reading from page 42”). The flag is set to <code>true</code> only after that initial scroll completes.
      </p>

      <p>
        In vertical mode, however, the <strong>scroll‑to‑initial‑page</strong> logic and the <strong>scroll‑event handlers</strong> that detect vertical vs. horizontal movement were stepping on each other. The flag could remain <code>false</code> longer than expected, causing the IntersectionObserver to ignore all intersection changes—including those that happen during normal vertical scrolling. When the flag finally flipped to <code>true</code>, the observer would “see” only the first page as intersecting, and the UI would snap back to page 1.
      </p>

      <h2>The Fix: Remove the Guard for Vertical Scrolling</h2>

      <p>
        The solution was two‑fold:
      </p>

      <ol>
        <li><strong>Remove the <code>hasScrolledToInitialPage</code> guard for vertical scrolling.</strong> The observer should start tracking pages immediately in vertical mode, because there’s no initial “page‑jump” to wait for—vertical scrolling is continuous.</li>
        <li><strong>Add <code>readingMode !== 'horizontal'</code> checks to all swipe/touch handlers</strong> to ensure vertical‑scroll events don’t accidentally trigger horizontal‑page‑turn logic.</li>
      </ol>

      <h3>1. Adjusting the IntersectionObserver</h3>

      <p>
        The guard was simply removed from the observer callback:
      </p>

<pre><code>{`useEffect(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      // ✅ No guard – observer always active
      // … track visible page …
    },
    { threshold: 0.5 }
  );

  // Observe each page element
  pages.forEach((page) => observer.observe(page));

  return () => observer.disconnect();
}, []); // ✅ No dependency on hasScrolledToInitialPage`}</code></pre>

      <p>
        Now the observer begins tracking as soon as the pages are rendered, regardless of whether the initial scroll has completed. This eliminates the race condition and ensures the UI always knows which page is visible.
      </p>

      <h3>2. Guarding the Swipe Handlers</h3>

      <p>
        To prevent vertical scroll gestures from being misinterpreted as horizontal swipes, each touch/mouse handler received a mode check:
      </p>

<pre><code>{`const handleTouchStart = (e) => {
  if (readingMode !== 'horizontal') return; // ✅ Only react in horizontal mode
  // … record touch position …
};

const handleTouchMove = (e) => {
  if (readingMode !== 'horizontal') return;
  // … detect swipe direction …
};

// Same for handleTouchEnd, handleMouseDown, handleMouseUp`}</code></pre>

      <p>
        This ensures that vertical‑scroll events pass through untouched, while horizontal‑mode swipes still trigger page‑turns as intended.
      </p>

      <h2>Why the Bug Was Hard to Spot</h2>

      <p>
        This kind of bug is easy to miss in development because:
      </p>

      <ul>
        <li><strong>It’s timing‑dependent.</strong> On a fast machine or with a small book, the initial scroll might finish before the user starts scrolling manually, so the bug never surfaces.</li>
        <li><strong>It’s mode‑specific.</strong> Horizontal mode worked perfectly; the bug only appeared in vertical mode, which is less frequently tested during development.</li>
        <li><strong>It looks like a scroll‑reset.</strong> The snap‑back feels like the browser’s native “scroll‑to‑top” behavior, which can lead you to suspect CSS, JavaScript scroll‑position restoration, or even browser bugs.</li>
      </ul>

      <p>
        The key to diagnosing it was isolating the conditions: <em>“Only in vertical mode, only after a pause, only when the initial scroll hasn’t yet completed.”</em> Once I could reproduce it reliably, the console logs showed that <code>hasScrolledToInitialPage</code> was still <code>false</code> when the snap‑back happened, pointing directly at the IntersectionObserver guard.
      </p>

      <h2>Lessons Learned</h2>

      <h3>1. Be Wary of “Wait‑for‑Initial‑Action” Flags in Scroll‑Tracking Code</h3>
      <p>
        Flags that delay observer activation create a window of vulnerability where the UI’s understanding of the scroll position can diverge from reality. If you must wait for an initial scroll, consider using a timeout or a more robust “first intersection” detection instead of a simple boolean.
      </p>

      <h3>2. Separate Scroll‑Mode Logic Clearly</h3>
      <p>
        Mixing horizontal‑swipe and vertical‑scroll logic in the same handlers invites subtle bugs. The fix here was to add explicit mode checks, but a cleaner architecture would be to split the handlers entirely—one set for horizontal, another for vertical—so they don’t interfere.
      </p>

      <h3>3. Test Edge Cases in Both Scroll Modes</h3>
      <p>
        It’s tempting to develop and test primarily in the “default” mode (horizontal swipes for a book reader). Make a habit of switching to the alternative mode (vertical scroll) and verifying that basic interactions—scrolling, tapping, resizing—work as expected.
      </p>

      <h2>Outcome</h2>

      <p>
        After deploying the fix, vertical scrolling in the Chatty Books reader is smooth and stable. The IntersectionObserver now tracks pages from the moment the book loads, and the swipe‑handlers stay out of the way when the reader is in vertical mode. The bug is resolved, and the reader retains its position correctly across sessions.
      </p>

      <p>
        This kind of subtle front‑end bug is a reminder that scroll‑related code is inherently stateful and timing‑sensitive. By keeping scroll‑tracking logic simple and minimizing dependencies on transient flags, you can avoid a whole class of frustrating, hard‑to‑reproduce UI glitches.
      </p>

      <p>
        —<br />
        Patrick
      </p>
    </div>
  );
};

export default FixingVerticalScrollSnapBackBugChattyBooksReader;